import React, { useCallback, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, QrCode, X } from "lucide-react";
import { api, formatError, fmtDateTime } from "../../lib/api";
import { AdminHeader, AdminModal, AField, ATextarea, toLocalInput, fromLocalInput } from "../../components/AdminLayout";
import ColorPicker from "../../components/ColorPicker";
import ImageDropzone from "../../components/ImageDropzone";
import MapConfirm from "../../components/MapConfirm";

const BLANK = { title: "", location: "", venue: "", description: "", about: "", image: "", starts_at: "", color: "#D4AF37", category: "events", ticket_package_id: "", media: [], capacity: "", show_availability: false };

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [modal, setModal] = useState(null);
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/events").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); api.get("/admin/packages").then((r) => setPackages(r.data)).catch(() => {}); }, [load]);

  const setDate = (i, v) => setModal((m) => ({ ...m, dates: m.dates.map((d, j) => (j === i ? v : d)) }));
  const addDate = () => setModal((m) => ({ ...m, dates: [...(m.dates || []), ""] }));
  const removeDate = (i) => setModal((m) => ({ ...m, dates: m.dates.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true);
    const base = {
      title: modal.title, location: modal.location, venue: modal.venue || "", description: modal.description,
      about: modal.about || "", category: modal.category || "events",
      image: modal.image || undefined, color: modal.color,
      ticket_package_id: modal.ticket_package_id || "", media: modal.media || [],
      capacity: (modal.capacity === "" || modal.capacity == null) ? null : Number(modal.capacity),
      show_availability: !!modal.show_availability,
    };
    try {
      if (modal.id) {
        await api.put(`/admin/events/${modal.id}`, { ...base, starts_at: modal.starts_at ? fromLocalInput(modal.starts_at) : undefined });
        toast.success("Event saved");
      } else {
        const dates = (modal.dates || []).filter(Boolean);
        if (dates.length === 0) { toast.error("Add at least one date"); setBusy(false); return; }
        for (const d of dates) await api.post("/admin/events", { ...base, starts_at: fromLocalInput(d) });
        toast.success(`${dates.length} ${(modal.category === "socials" ? "social" : "event")}${dates.length > 1 ? "s" : ""} created`);
      }
      setModal(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (ev) => {
    if (!window.confirm(`Delete ${ev.title}?`)) return;
    try { await api.delete(`/admin/events/${ev.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openNew = () => setModal({ ...BLANK, dates: [toLocalInput(new Date().toISOString())] });

  return (
    <div>
      <AdminHeader title="Events" subtitle={`${items.length} total`}
        action={<button onClick={openNew} data-testid="admin-add-event" className="flex items-center gap-2 bg-[#D4AF37] text-black font-semibold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-transform"><Plus size={16} /> New event</button>} />

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((ev) => (
          <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
            {ev.image && <img src={ev.image} alt="" className="w-full h-32 object-cover" />}
            <div className="p-5">
              <h3 className="font-display text-xl font-semibold">{ev.title}</h3>
              <p className="text-[#A19DB0] text-xs mt-0.5">{fmtDateTime(ev.starts_at)} · {ev.location}</p>
              <p className="text-[#A19DB0] text-xs mt-1">{ev.joined_count} going</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setModal({ ...ev, media: ev.media || [], starts_at: toLocalInput(ev.starts_at) })} data-testid={`edit-event-${ev.id}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm hover:bg-white/10"><Pencil size={14} /> Edit</button>
                <button onClick={() => setQr(ev)} data-testid={`qr-event-${ev.id}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm hover:bg-white/10"><QrCode size={14} className="text-[#D4AF37]" /> QR</button>
                <button onClick={() => del(ev)} data-testid={`delete-event-${ev.id}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm text-[#E57373] hover:bg-white/10"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <AdminModal open onOpenChange={() => setModal(null)} title={modal.id ? "Edit event" : "New event"} onSubmit={save} busy={busy}>
          <AField label="Title" value={modal.title} onChange={(e) => setModal({ ...modal, title: e.target.value })} data-testid="event-title" />
          <div>
            <label className="text-xs text-[#A19DB0]">Category (for scanner grouping)</label>
            <select value={modal.category || "events"} onChange={(e) => setModal({ ...modal, category: e.target.value })} data-testid="event-category" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 text-sm outline-none focus:border-[#D4AF37]">
              <option value="events" className="bg-[#140F1E]">Event</option>
              <option value="socials" className="bg-[#140F1E]">Social</option>
            </select>
          </div>
          <AField label="Location (short)" value={modal.location} onChange={(e) => setModal({ ...modal, location: e.target.value })} data-testid="event-location" />
          <AField label="Venue (full name / address)" value={modal.venue || ""} onChange={(e) => setModal({ ...modal, venue: e.target.value })} data-testid="event-venue" placeholder="e.g. The Ballroom, 12 Club St" />
          <MapConfirm query={modal.venue || modal.location} testidPrefix="event-map" />
          <ATextarea label="Short description (card)" value={modal.description} onChange={(e) => setModal({ ...modal, description: e.target.value })} data-testid="event-desc" />
          <ATextarea label="About this event (detail page)" value={modal.about || ""} onChange={(e) => setModal({ ...modal, about: e.target.value })} data-testid="event-about" />
          <AField label="Cover image URL" value={modal.image} onChange={(e) => setModal({ ...modal, image: e.target.value })} data-testid="event-image" placeholder="https://…" />
          <ImageDropzone value={modal.image} onUploaded={(url) => setModal({ ...modal, image: url })} />
          <div>
            <label className="text-xs text-[#A19DB0]">Ticket package (shown on detail page)</label>
            <select value={modal.ticket_package_id || ""} onChange={(e) => setModal({ ...modal, ticket_package_id: e.target.value })} data-testid="event-ticket-package" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 text-sm outline-none focus:border-[#D4AF37]">
              <option value="" className="bg-[#140F1E]">None (fall back to Packages by category)</option>
              {packages.map((p) => <option key={p.id} value={p.id} className="bg-[#140F1E]">{p.name}{p.group ? ` · ${p.group}` : ""}{p.price != null ? ` · S$${p.price}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#A19DB0]">Photos & videos (detail gallery)</label>
            {(modal.media || []).length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2" data-testid="event-media-list">
                {(modal.media || []).map((m, i) => (
                  <div key={`media-${i}`} className="relative">
                    {m.match(/\.(mp4|webm|mov)(\?|$)/i)
                      ? <video src={m} className="w-full aspect-square object-cover rounded-lg" />
                      : <img src={m} alt="" className="w-full aspect-square object-cover rounded-lg" />}
                    <button type="button" onClick={() => setModal((mm) => ({ ...mm, media: mm.media.filter((_, j) => j !== i) }))} data-testid={`event-media-remove-${i}`} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#E57373] text-white flex items-center justify-center text-xs">✕</button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2"><ImageDropzone value="" onUploaded={(url) => setModal((mm) => ({ ...mm, media: [...(mm.media || []), url] }))} /></div>
          </div>
          {modal.id ? (
            <AField label="Starts at" type="datetime-local" value={modal.starts_at} onChange={(e) => setModal({ ...modal, starts_at: e.target.value })} data-testid="event-starts" />
          ) : (
            <div>
              <label className="text-xs text-[#A19DB0]">Dates — add several to create multiple sessions at once</label>
              {(modal.dates || []).map((d, i) => (
                <div key={i} className="flex gap-2 mt-1">
                  <input type="datetime-local" value={d} onChange={(e) => setDate(i, e.target.value)} data-testid={`event-date-${i}`} className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
                  {modal.dates.length > 1 && <button type="button" onClick={() => removeDate(i)} data-testid={`event-remove-date-${i}`} className="text-[#E57373] px-3">✕</button>}
                </div>
              ))}
              <button type="button" onClick={addDate} data-testid="event-add-date" className="text-[#D4AF37] text-sm mt-2">+ Add another date</button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 items-end">
            <AField label="Tickets to sell (blank = no limit)" type="number" value={modal.capacity ?? ""} onChange={(e) => setModal({ ...modal, capacity: e.target.value })} data-testid="event-capacity" placeholder="No limit" />
            <label className="flex items-center gap-2 text-sm bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 cursor-pointer">
              <input type="checkbox" checked={!!modal.show_availability} onChange={(e) => setModal({ ...modal, show_availability: e.target.checked })} data-testid="event-show-availability" className="w-4 h-4 accent-[#D4AF37]" />
              Show members slots left
            </label>
          </div>
          <ColorPicker value={modal.color} onChange={(v) => setModal((m) => ({ ...m, color: v }))} />
        </AdminModal>
      )}

      {qr && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setQr(null)} data-testid="event-qr-modal">
          <div className="relative bg-[#140F1E] border border-white/10 rounded-3xl p-8 text-center max-w-xs" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setQr(null)} className="absolute top-3 right-3 text-[#A19DB0] hover:text-white"><X size={18} /></button>
            <p className="font-display text-xl font-semibold mb-1">{qr.title}</p>
            <p className="text-[#A19DB0] text-xs mb-5">Members scan to check in</p>
            <div className="bg-white p-4 rounded-2xl inline-block">
              <QRCode value={JSON.stringify({ ref_type: "event", ref_id: qr.id })} size={200} fgColor="#0D0B14" />
            </div>
            <p className="text-[#A19DB0] text-xs mt-4">EVENT-{qr.id.slice(0, 8)}</p>
          </div>
        </div>
      )}
    </div>
  );
}
